import { useEffect, useState, useCallback } from "react";
import api, { gbp } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { XeroResyncDialog } from "@/components/XeroResyncDialog";

const TABS = [
  { key: "renewals", label: "Renewals forecast", endpoint: "renewals-forecast", pdf: "renewals", period: false },
  { key: "pipeline", label: "Proposal pipeline & conversion", endpoint: "pipeline", pdf: "pipeline", period: true },
  { key: "conversion", label: "Sales pipeline conversion", endpoint: "pipeline-conversion", pdf: null, period: true },
  { key: "leadsources", label: "Lead source performance", endpoint: "lead-sources", pdf: null, period: true },
  { key: "mrr", label: "MRR movement", endpoint: "mrr-movement", pdf: "mrr", period: true },
  { key: "winloss", label: "Win / loss", endpoint: "win-loss", pdf: "winloss", period: true },
  { key: "owners", label: "Team / owner performance", endpoint: "owner-performance", pdf: "owners", period: true },
  { key: "clientbase", label: "Client base overview", endpoint: "client-base", pdf: "clientbase", period: false },
  { key: "leads", label: "Pipeline leads", endpoint: "pipeline-leads", pdf: "leads", period: true },
  { key: "won", label: "Won proposals breakdown", endpoint: "won-breakdown", pdf: "won", period: true, xlsx: "won-breakdown.xlsx" },
  { key: "xeroexc", label: "Xero sync exceptions", endpoint: "xero-exceptions", pdf: null, period: true },
  { key: "oneoffbooked", label: "One-off fees booked", endpoint: "oneoff-booked", pdf: null, period: true },
  { key: "chlog", label: "Companies House change log", endpoint: "ch-change-log", pdf: null, period: false },
];

const PERIODS = [
  { key: "last_30d", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "quarter", label: "This quarter" },
  { key: "ytd", label: "Year to date" },
  { key: "last_12m", label: "Last 12 months" },
  { key: "custom", label: "Custom range" },
];

const toneCls = {
  green: "text-emerald-600", red: "text-red-600", amber: "text-amber-600",
  teal: "text-cass-teal", navy: "text-cass-navy",
};

const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const signed = (n) => (n > 0 ? "+" : n < 0 ? "\u2212" : "") + gbp(Math.abs(Number(n || 0)));
const titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Kpi = ({ label, value, tone, testId }) => (
  <Card className="p-4 rounded-2xl border-border" data-testid={testId}>
    <div className={`text-2xl font-bold font-alt tabular leading-none ${toneCls[tone] || "text-cass-navy"}`}>{value}</div>
    <div className="text-[11px] text-cass-slate mt-1.5">{label}</div>
  </Card>
);

const toCsv = (name, head, lines) => {
  const csv = [head, ...lines].map((a) => a.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};

export default function Reports() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.key === t) ? t : "renewals";
  });
  const [period, setPeriod] = useState(() => localStorage.getItem("cass.reportsPeriod") || "last_12m");
  const [from, setFrom] = useState(() => localStorage.getItem("cass.reportsFrom") || "");
  const [to, setTo] = useState(() => localStorage.getItem("cass.reportsTo") || "");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [xlsxBusy, setXlsxBusy] = useState(false);

  const cfg = TABS.find((t) => t.key === tab);
  const cur = data[tab];

  const params = useCallback(() => {
    if (!cfg.period) return {};
    const p = { period };
    if (period === "custom") { p.from = from; p.to = to; }
    return p;
  }, [cfg, period, from, to]);

  const load = useCallback(() => {
    if (period === "custom" && cfg.period && (!from || !to)) return;
    setLoading(true);
    api.get(`/reports/${cfg.endpoint}`, { params: params() })
      .then((r) => setData((d) => ({ ...d, [tab]: r.data })))
      .catch(() => toast.error("Could not load report"))
      .finally(() => setLoading(false));
  }, [tab, cfg, period, from, to, params]);

  useEffect(() => { load(); }, [load]);

  // Remember the period picker globally so every report reopens how you left it.
  useEffect(() => { try { localStorage.setItem("cass.reportsPeriod", period); } catch { /* ignore */ } }, [period]);
  useEffect(() => { try { localStorage.setItem("cass.reportsFrom", from); } catch { /* ignore */ } }, [from]);
  useEffect(() => { try { localStorage.setItem("cass.reportsTo", to); } catch { /* ignore */ } }, [to]);

  const downloadXlsx = async () => {
    setXlsxBusy(true);
    try {
      const res = await api.get(`/reports/${cfg.xlsx}`, { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "CASS-won-proposals.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not generate Excel"); }
    finally { setXlsxBusy(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/reports/${cfg.pdf}/pdf`, { params: params(), responseType: "blob" });      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a"); a.href = url; a.download = `CASS-${cfg.pdf}-report.pdf`; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not generate PDF"); }
    finally { setPdfBusy(false); }
  };

  const exportCsv = () => {
    if (!cur) return;
    if (tab === "renewals") {
      toCsv("CASS-renewals-forecast.csv",
        ["Reference", "Client", "Owner", "MRR", "Renewal date", "Days until", "Migrated"],
        cur.rows.map((r) => [r.reference, r.client_name, r.owner_name || "", (r.totals?.monthly_recurring || 0).toFixed(2), r.renewal_date || "", r.days_until, r.migrated ? "Yes" : "No"]));
    } else if (tab === "pipeline") {
      toCsv("CASS-pipeline.csv",
        ["Reference", "Client", "Owner", "Type", "Status", "Sent", "MRR", "Days to sign"],
        cur.rows.map((r) => [r.reference, r.client_name, r.owner_name || "", r.type, r.status, r.sent_at || "", r.mrr.toFixed(2), r.days_to_sign ?? ""]));
    } else if (tab === "mrr") {
      toCsv("CASS-mrr-movement.csv",
        ["Reference", "Client", "Owner", "Type", "Date", "MRR", "Change"],
        cur.rows.map((r) => [r.reference, r.client_name, r.owner_name || "", r.kind, r.date || "", r.mrr.toFixed(2), r.delta.toFixed(2)]));
    } else if (tab === "winloss") {
      toCsv("CASS-win-loss.csv",
        ["Reference", "Client", "Owner", "Type", "Outcome", "Date", "MRR", "Reason"],
        cur.rows.map((r) => [r.reference, r.client_name, r.owner_name || "", r.type, r.outcome, r.date || "", r.mrr.toFixed(2), r.reason || ""]));
    } else if (tab === "conversion") {
      toCsv("CASS-sales-conversion.csv",
        ["Stage", "Reached", "Expected MRR", "Step conversion %"],
        cur.funnel.map((f) => [f.stage, f.count, f.mrr.toFixed(2), f.step_rate]));
    } else if (tab === "leadsources") {
      toCsv("CASS-lead-sources.csv",
        ["Lead source", "Total", "Open", "Won", "Lost", "Nurture", "Win rate %", "Expected MRR", "Won MRR"],
        cur.rows.map((r) => [r.source, r.total, r.open, r.won, r.lost, r.nurture, r.win_rate, r.expected_mrr.toFixed(2), r.won_mrr.toFixed(2)]));
    } else if (tab === "owners") {
      toCsv("CASS-owner-performance.csv",
        ["Owner", "Sent", "Accepted", "Conversion %", "MRR won", "One-off won", "Avg days to sign"],
        cur.rows.map((r) => [r.owner, r.sent, r.accepted, r.conversion, r.mrr_won.toFixed(2), r.oneoff_won.toFixed(2), r.avg_days]));
    } else if (tab === "clientbase") {
      toCsv("CASS-client-base.csv",
        ["Client", "Type", "MRR", "One-off", "Renewal date", "Proposal ref"],
        cur.rows.map((r) => [r.client_name, r.type, r.mrr.toFixed(2), r.oneoff.toFixed(2), r.renewal_date || "", r.reference]));
    } else if (tab === "won") {
      toCsv("CASS-won-proposals.csv",
        ["Client", "Proposal #", "Monthly fee", "Repeating invoice created", "New Client Onboarding fee", "Onboarding Xero inv #", "Onboarding inv date", "Alignment fees due", "Alignment Xero inv #", "Alignment inv date", "One-off fees", "One-off Xero inv #", "One-off inv date"],
        cur.rows.map((r) => [r.client, r.reference, r.monthly.toFixed(2), r.repeating_created ? "Yes" : "No", r.onboarding_fee.toFixed(2), r.onboarding_inv, r.onboarding_date, r.alignment_fee.toFixed(2), r.alignment_inv, r.alignment_date, r.oneoff_fee.toFixed(2), r.oneoff_inv, r.oneoff_date]));
    } else if (tab === "xeroexc") {
      toCsv("CASS-xero-exceptions.csv",
        ["Reference", "Client", "Owner", "Accepted", "Type", "Issue", "Monthly", "One-off"],
        cur.rows.map((r) => [r.reference, r.client, r.owner, r.accepted_date, r.kind, r.issue, r.monthly.toFixed(2), r.oneoff.toFixed(2)]));
    } else if (tab === "oneoffbooked") {
      toCsv("CASS-oneoff-booked.csv",
        ["Client", "Proposal #", "Owner", "Accepted", "One-off fee", "Booked", "Xero inv #", "Inv date"],
        cur.rows.map((r) => [r.client, r.reference, r.owner, r.accepted_date, r.oneoff_fee.toFixed(2), r.booked ? "Yes" : "No", r.invoice_number, r.invoice_date]));
    } else if (tab === "chlog") {
      toCsv("CASS-companies-house-changes.csv",
        ["Date", "Client", "Company number", "Field", "Old", "New", "Reviewed"],
        cur.rows.map((r) => [r.date, r.client, r.company_number, r.field, r.old, r.new, r.reviewed ? "Yes" : "No"]));
    } else {
      toCsv("CASS-pipeline-leads.csv",
        ["Company", "Contact", "Owner", "Stage", "Expected fee", "Close date"],
        cur.rows.map((r) => [r.company, r.contact_name || "", r.owner_name || "", r.stage, r.expected_fee.toFixed(2), r.close_date || ""]));
    }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Internal commercial & operational reporting for the practice." accent="navy" Icon={BarChart3}
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} disabled={!cur} data-testid="report-export-csv"><Download className="w-4 h-4 mr-2" />CSV</Button>
            {cfg.xlsx && <Button variant="outline" onClick={downloadXlsx} disabled={xlsxBusy || !cur} data-testid="report-export-xlsx"><Download className="w-4 h-4 mr-2" />{xlsxBusy ? "Building\u2026" : "Excel"}</Button>}
            <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy || !cur || !cfg.pdf} data-testid="report-export-pdf"><FileText className="w-4 h-4 mr-2" />{pdfBusy ? "Building\u2026" : "PDF"}</Button>
          </>
        } />
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="report-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`report-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${tab === t.key ? "bg-cass-navy text-white border-cass-navy" : "bg-white text-cass-slate border-border hover:border-cass-teal"}`}>
              {t.label}
            </button>
          ))}
          {cfg.period && (
            <div className="flex items-center gap-2 ml-auto" data-testid="report-period">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[170px] h-9" data-testid="report-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
              {period === "custom" && (
                <>
                  <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="report-from" />
                  <span className="text-cass-slate text-sm">to</span>
                  <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="report-to" />
                </>
              )}
            </div>
          )}
        </div>

        {loading && !cur ? (
          <Card className="p-10 rounded-2xl border-border text-center text-cass-slate" data-testid="report-loading">Loading report…</Card>
        ) : !cur ? (
          <Card className="p-10 rounded-2xl border-border text-center text-cass-slate">Choose a period to view this report.</Card>
        ) : tab === "renewals" ? (
          <RenewalsReport d={cur} />
        ) : tab === "pipeline" ? (
          <PipelineReport d={cur} />
        ) : tab === "conversion" ? (
          <ConversionReport d={cur} />
        ) : tab === "leadsources" ? (
          <LeadSourceReport d={cur} />
        ) : tab === "mrr" ? (
          <MrrReport d={cur} />
        ) : tab === "winloss" ? (
          <WinLossReport d={cur} />
        ) : tab === "owners" ? (
          <OwnersReport d={cur} />
        ) : tab === "clientbase" ? (
          <ClientBaseReport d={cur} />
        ) : tab === "won" ? (
          <WonReport d={cur} onReload={load} />
        ) : tab === "xeroexc" ? (
          <XeroExceptionsReport d={cur} onReload={load} />
        ) : tab === "oneoffbooked" ? (
          <OneoffBookedReport d={cur} />
        ) : tab === "chlog" ? (
          <ChChangeLogReport d={cur} />
        ) : (
          <LeadsReport d={cur} />
        )}
      </div>
    </div>
  );
}

function WonReport({ d, onReload }) {
  const t = d.totals || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-won-count" value={t.count || 0} label="Proposals won" tone="green" />
        <Kpi testId="kpi-won-monthly" value={gbp(t.monthly)} label="Monthly fees" tone="navy" />
        <Kpi testId="kpi-won-onboarding" value={gbp(t.onboarding)} label="Onboarding fees" tone="teal" />
        <Kpi testId="kpi-won-alignment" value={gbp(t.alignment)} label="Alignment fees" tone="amber" />
        <Kpi testId="kpi-won-oneoff" value={gbp(t.oneoff)} label="One-off fees" tone="navy" />
      </div>
      <Card className="rounded-2xl border-border overflow-x-auto mt-4" data-testid="won-report-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Proposal #</TableHead>
            <TableHead className="text-right">Monthly</TableHead><TableHead>Repeating inv</TableHead>
            <TableHead className="text-right">Onboarding</TableHead><TableHead>Onboarding inv</TableHead>
            <TableHead className="text-right">Alignment</TableHead><TableHead>Alignment inv</TableHead>
            <TableHead className="text-right">One-off</TableHead><TableHead>One-off inv</TableHead>
            <TableHead className="text-right">Xero</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={i} data-testid={`won-row-${i}`}>
                <TableCell className="font-medium text-cass-navy">{r.client}</TableCell>
                <TableCell>{r.reference}</TableCell>
                <TableCell className="text-right">{gbp(r.monthly)}</TableCell>
                <TableCell>{r.repeating_created ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">{gbp(r.onboarding_fee)}</TableCell>
                <TableCell className="text-xs text-cass-slate">{r.onboarding_inv}{r.onboarding_date ? ` · ${fmtD(r.onboarding_date)}` : ""}</TableCell>
                <TableCell className="text-right">{gbp(r.alignment_fee)}</TableCell>
                <TableCell className="text-xs text-cass-slate">{r.alignment_inv}{r.alignment_date ? ` · ${fmtD(r.alignment_date)}` : ""}</TableCell>
                <TableCell className="text-right">{gbp(r.oneoff_fee)}</TableCell>
                <TableCell className="text-xs text-cass-slate">{r.oneoff_inv}{r.oneoff_date ? ` · ${fmtD(r.oneoff_date)}` : ""}</TableCell>
                <TableCell className="text-right">
                  {r.id && <XeroResyncDialog proposalId={r.id} reference={r.reference} onDone={onReload} />}
                </TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={11} className="text-center py-10 text-cass-slate">No proposals won in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function XeroExceptionsReport({ d, onReload }) {
  const t = d.totals || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testId="kpi-xe-count" value={t.count || 0} label="Open exceptions" tone="red" />
        <Kpi testId="kpi-xe-errors" value={t.errors || 0} label="Sync failures" tone="red" />
        <Kpi testId="kpi-xe-unsynced" value={t.unsynced || 0} label="Not synced yet" tone="amber" />
        <Kpi testId="kpi-xe-exposed" value={gbp(t.exposed_monthly)} label="Monthly not invoiced" tone="navy" />
      </div>
      <Card className="rounded-2xl border-border overflow-x-auto mt-4" data-testid="xero-exceptions-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Proposal #</TableHead><TableHead>Owner</TableHead>
            <TableHead>Accepted</TableHead><TableHead>Issue</TableHead>
            <TableHead className="text-right">Monthly</TableHead><TableHead className="text-right">One-off</TableHead>
            <TableHead className="text-right">Fix</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.id || i} data-testid={`xero-exc-row-${i}`}>
                <TableCell className="font-medium text-cass-navy">{r.client}</TableCell>
                <TableCell>{r.reference}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner || "\u2014"}</TableCell>
                <TableCell className="text-sm text-cass-slate">{fmtD(r.accepted_date)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${r.kind === "error" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.kind === "error" ? "Failed" : "Not synced"}</Badge>
                  <div className="text-[11px] text-cass-slate mt-0.5 max-w-[320px]">{r.issue}</div>
                </TableCell>
                <TableCell className="text-right tabular">{gbp(r.monthly)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.oneoff)}</TableCell>
                <TableCell className="text-right">
                  {r.id && <XeroResyncDialog proposalId={r.id} reference={r.reference} onDone={onReload} />}
                </TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={8} className="text-center py-10 text-cass-slate">No Xero sync exceptions in this period. 🎉</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function OneoffBookedReport({ d }) {
  const t = d.totals || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-ob-count" value={t.count || 0} label="With one-off fees" tone="navy" />
        <Kpi testId="kpi-ob-booked" value={t.booked || 0} label="Invoice booked" tone="green" />
        <Kpi testId="kpi-ob-notbooked" value={t.not_booked || 0} label="Awaiting invoice" tone="amber" />
        <Kpi testId="kpi-ob-fees" value={gbp(t.fees_total)} label="One-off fees due" tone="navy" />
        <Kpi testId="kpi-ob-bookedtotal" value={gbp(t.booked_total)} label="Booked value" tone="green" />
      </div>
      <Card className="rounded-2xl border-border overflow-x-auto mt-4" data-testid="oneoff-booked-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Proposal #</TableHead><TableHead>Owner</TableHead>
            <TableHead>Accepted</TableHead><TableHead className="text-right">One-off fee</TableHead>
            <TableHead>Booked</TableHead><TableHead>Xero inv #</TableHead><TableHead>Inv date</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.id || i} data-testid={`oneoff-booked-row-${i}`}>
                <TableCell className="font-medium text-cass-navy">{r.client}</TableCell>
                <TableCell>{r.reference}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner || "\u2014"}</TableCell>
                <TableCell className="text-sm text-cass-slate">{fmtD(r.accepted_date)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.oneoff_fee)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${r.booked ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.booked ? "Yes" : "No"}</Badge>
                </TableCell>
                <TableCell className="text-sm text-cass-slate">{r.invoice_number || "\u2014"}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.invoice_date || "\u2014"}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={8} className="text-center py-10 text-cass-slate">No one-off fees won in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function ChChangeLogReport({ d }) {
  const t = d.totals || {};
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Kpi testId="kpi-ch-count" value={t.count || 0} label="Recorded changes" tone="navy" />
        <Kpi testId="kpi-ch-clients" value={t.clients || 0} label="Clients affected" tone="teal" />
        <Kpi testId="kpi-ch-flagged" value={t.flagged || 0} label="Awaiting review" tone="amber" />
      </div>
      <Card className="rounded-2xl border-border overflow-x-auto mt-4" data-testid="ch-changelog-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead>Company #</TableHead>
            <TableHead>Field</TableHead><TableHead>Old</TableHead><TableHead>New</TableHead><TableHead>Reviewed</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={i} data-testid={`ch-log-row-${i}`}>
                <TableCell className="text-sm text-cass-slate">{fmtD(r.date)}</TableCell>
                <TableCell className="font-medium text-cass-navy">{r.client}</TableCell>
                <TableCell className="text-sm text-cass-slate tabular">{r.company_number || "\u2014"}</TableCell>
                <TableCell className="text-sm">{r.field}</TableCell>
                <TableCell className="text-sm text-cass-slate max-w-[200px] truncate" title={r.old}>{r.old || "\u2014"}</TableCell>
                <TableCell className="text-sm text-cass-navy max-w-[200px] truncate" title={r.new}>{r.new || "\u2014"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${r.reviewed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.reviewed ? "Yes" : "Pending"}</Badge>
                </TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-cass-slate">No Companies House changes recorded yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function RenewalsReport({ d }) {
  const tone = { overdue: "red", d30: "red", d60: "amber", d90: "green" };
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {d.buckets.map((b) => (
          <Kpi key={b.key} testId={`report-bucket-${b.key}`} tone={tone[b.key]}
            value={b.count} label={`${b.label} \u00b7 ${gbp(b.mrr)}/mo`} />
        ))}
      </div>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reference</TableHead><TableHead>Client</TableHead><TableHead>Owner</TableHead>
            <TableHead className="text-right">MRR</TableHead><TableHead className="text-right">Renewal due</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r) => (
              <TableRow key={r.id || r.client_id} data-testid={`report-renewal-${r.id || r.client_id}`}>
                <TableCell className="font-medium text-cass-navy">{r.reference}{r.migrated && <Badge variant="outline" className="ml-2 text-[10px] border-slate-300 text-cass-slate">No proposal</Badge>}</TableCell>
                <TableCell>{r.client_name}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner_name || "\u2014"}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.totals?.monthly_recurring)}</TableCell>
                <TableCell className={`text-right tabular ${r.days_until <= 30 ? "text-red-600" : "text-cass-slate"}`}>{r.days_until < 0 ? `${Math.abs(r.days_until)}d overdue` : `in ${r.days_until}d`}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={5} className="text-center py-10 text-cass-slate">No renewals in the next 90 days.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function PipelineReport({ d }) {
  const max = Math.max(...d.funnel.map((f) => f.count), 1);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi testId="kpi-sent" value={d.sent} label="Proposals sent" tone="navy" />
        <Kpi testId="kpi-viewed" value={`${d.viewed} (${d.view_rate}%)`} label="Viewed" tone="teal" />
        <Kpi testId="kpi-accepted" value={d.accepted} label="Accepted" tone="green" />
        <Kpi testId="kpi-conv" value={`${d.conversion_rate}%`} label="Conversion rate" tone="green" />
        <Kpi testId="kpi-days" value={d.avg_days_to_sign} label="Avg days to sign" tone="navy" />
        <Kpi testId="kpi-openmrr" value={gbp(d.open_mrr)} label="Open pipeline MRR" tone="amber" />
      </div>
      <Card className="p-6 rounded-2xl border-border" data-testid="pipeline-funnel">
        <h3 className="font-alt font-semibold text-cass-navy mb-4">Conversion funnel</h3>
        <div className="space-y-3">
          {d.funnel.map((f) => (
            <div key={f.stage} className="flex items-center gap-3">
              <div className="w-32 text-sm text-cass-slate shrink-0">{f.stage}</div>
              <div className="flex-1 bg-cass-bone rounded-full h-6 overflow-hidden">
                <div className="h-6 bg-cass-teal/80 rounded-full flex items-center justify-end px-2" style={{ width: `${Math.max(6, (f.count / max) * 100)}%` }}>
                  <span className="text-[11px] font-semibold text-white">{f.count}</span>
                </div>
              </div>
              <div className="w-24 text-right text-sm tabular text-cass-navy">{gbp(f.mrr)}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reference</TableHead><TableHead>Client</TableHead><TableHead>Owner</TableHead>
            <TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Sent</TableHead>
            <TableHead className="text-right">MRR</TableHead><TableHead className="text-right">Days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.reference + i} data-testid={`pipeline-row-${i}`}>
                <TableCell className="font-medium text-cass-navy">{r.reference}</TableCell>
                <TableCell>{r.client_name}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner_name || "\u2014"}</TableCell>
                <TableCell className="text-sm">{titleCase(r.type)}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{titleCase(r.status)}</Badge></TableCell>
                <TableCell className="text-sm text-cass-slate">{fmtD(r.sent_at)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.mrr)}</TableCell>
                <TableCell className="text-right tabular text-cass-slate">{r.days_to_sign ?? "\u2014"}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={8} className="text-center py-10 text-cass-slate">No proposals sent in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function ConversionReport({ d }) {
  const max = Math.max(...d.funnel.map((f) => f.count), 1);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-conv-open" value={d.open_count} label="Open opportunities" tone="navy" />
        <Kpi testId="kpi-conv-won" value={d.won_count} label="Won" tone="green" />
        <Kpi testId="kpi-conv-lost" value={d.lost_count} label="Lost" tone="red" />
        <Kpi testId="kpi-conv-nurture" value={d.nurture_count} label="Nurture" tone="amber" />
        <Kpi testId="kpi-conv-overall" value={`${d.overall_conversion}%`} label="Enquiry → Won" tone="green" />
      </div>
      <Card className="p-6 rounded-2xl border-border" data-testid="conversion-funnel">
        <h3 className="font-alt font-semibold text-cass-navy mb-4">Stage-to-stage conversion</h3>
        <div className="space-y-3">
          {d.funnel.map((f, i) => (
            <div key={f.stage} className="flex items-center gap-3" data-testid={`conversion-row-${i}`}>
              <div className="w-40 text-sm text-cass-slate shrink-0">{f.stage}</div>
              <div className="flex-1 bg-cass-bone rounded-full h-6 overflow-hidden">
                <div className={`h-6 rounded-full flex items-center justify-end px-2 ${i === d.funnel.length - 1 ? "bg-emerald-500/80" : "bg-fuchsia-500/80"}`} style={{ width: `${Math.max(6, (f.count / max) * 100)}%` }}>
                  <span className="text-[11px] font-semibold text-white">{f.count}</span>
                </div>
              </div>
              <div className="w-16 text-right text-sm tabular text-cass-slate">{i === 0 ? "—" : `${f.step_rate}%`}</div>
              <div className="w-24 text-right text-sm tabular text-cass-navy">{gbp(f.mrr)}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-cass-slate mt-4">Snapshot of the live board: each bar counts opportunities that have reached that stage (Won reached every stage). The % is conversion from the previous stage.</p>
      </Card>
    </>
  );
}

function LeadSourceReport({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testId="kpi-ls-total" value={d.total} label="Enquiries (period)" tone="navy" />
        <Kpi testId="kpi-ls-sources" value={d.rows.length} label="Active sources" tone="teal" />
        <Kpi testId="kpi-ls-won" value={d.won_total} label="Won (period)" tone="green" />
        <Kpi testId="kpi-ls-wonmrr" value={gbp(d.won_mrr_total)} label="Won MRR" tone="green" />
      </div>
      <Card className="rounded-2xl border-border overflow-hidden" data-testid="leadsource-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Lead source</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Open</TableHead><TableHead className="text-right">Won</TableHead>
            <TableHead className="text-right">Lost</TableHead><TableHead className="text-right">Nurture</TableHead>
            <TableHead className="text-right">Win rate</TableHead><TableHead className="text-right">Expected MRR</TableHead>
            <TableHead className="text-right">Won MRR</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.source + i} data-testid={`leadsource-row-${i}`}>
                <TableCell className="text-cass-navy font-medium">{r.source}</TableCell>
                <TableCell className="text-right tabular">{r.total}</TableCell>
                <TableCell className="text-right tabular text-cass-slate">{r.open}</TableCell>
                <TableCell className="text-right tabular text-emerald-600">{r.won}</TableCell>
                <TableCell className="text-right tabular text-red-600">{r.lost}</TableCell>
                <TableCell className="text-right tabular text-amber-600">{r.nurture}</TableCell>
                <TableCell className="text-right tabular">{r.win_rate}%</TableCell>
                <TableCell className="text-right tabular">{gbp(r.expected_mrr)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.won_mrr)}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={9} className="text-center py-10 text-cass-slate">No enquiries in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function MrrReport({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi testId="kpi-new" value={gbp(d.new_mrr)} label="New business MRR" tone="green" />
        <Kpi testId="kpi-uplift" value={signed(d.renewal_uplift)} label="Renewal uplift" tone="teal" />
        <Kpi testId="kpi-var" value={signed(d.variation_mrr)} label="Variations" tone="teal" />
        <Kpi testId="kpi-churn" value={signed(-d.churn)} label="Churn" tone="red" />
        <Kpi testId="kpi-net" value={signed(d.net_movement)} label="Net movement" tone={d.net_movement >= 0 ? "green" : "red"} />
        <Kpi testId="kpi-live" value={gbp(d.live_mrr)} label="Live recurring MRR" tone="navy" />
      </div>
      <Card className="rounded-2xl border-border overflow-hidden" data-testid="mrr-summary">
        <Table>
          <TableHeader><TableRow><TableHead>Movement</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">MRR impact</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.movement.map((m, i) => (
              <TableRow key={m.type} className={i === d.movement.length - 1 ? "font-bold bg-cass-teal/5" : ""} data-testid={`mrr-move-${i}`}>
                <TableCell className="text-cass-navy">{m.type}</TableCell>
                <TableCell className="text-right tabular">{m.count}</TableCell>
                <TableCell className={`text-right tabular ${m.mrr < 0 ? "text-red-600" : m.mrr > 0 ? "text-emerald-600" : "text-cass-slate"}`}>{signed(m.mrr)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reference</TableHead><TableHead>Client</TableHead><TableHead>Owner</TableHead>
            <TableHead>Type</TableHead><TableHead>Date</TableHead>
            <TableHead className="text-right">MRR</TableHead><TableHead className="text-right">Change</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.reference + i} data-testid={`mrr-row-${i}`}>
                <TableCell className="font-medium text-cass-navy">{r.reference}</TableCell>
                <TableCell>{r.client_name}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner_name || "\u2014"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.kind}</Badge></TableCell>
                <TableCell className="text-sm text-cass-slate">{fmtD(r.date)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.mrr)}</TableCell>
                <TableCell className={`text-right tabular ${r.delta < 0 ? "text-red-600" : "text-emerald-600"}`}>{signed(r.delta)}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-cass-slate">No MRR movement in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function WinLossReport({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-won" value={d.won} label="Won" tone="green" />
        <Kpi testId="kpi-lost" value={d.lost} label="Lost / expired" tone="red" />
        <Kpi testId="kpi-winrate" value={`${d.win_rate}%`} label="Win rate" tone="green" />
        <Kpi testId="kpi-wonmrr" value={gbp(d.won_mrr)} label="MRR won" tone="green" />
        <Kpi testId="kpi-lostmrr" value={gbp(d.lost_mrr)} label="MRR lost" tone="red" />
      </div>
      <Card className="rounded-2xl border-border overflow-hidden" data-testid="winloss-owner">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Owner</TableHead><TableHead className="text-right">Won</TableHead><TableHead className="text-right">Lost</TableHead>
            <TableHead className="text-right">Win rate</TableHead><TableHead className="text-right">MRR won</TableHead><TableHead className="text-right">MRR lost</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.by_owner.map((o) => (
              <TableRow key={o.owner} data-testid={`winloss-owner-${o.owner}`}>
                <TableCell className="text-cass-navy">{o.owner}</TableCell>
                <TableCell className="text-right tabular text-emerald-600">{o.won}</TableCell>
                <TableCell className="text-right tabular text-red-600">{o.lost}</TableCell>
                <TableCell className="text-right tabular">{o.win_rate}%</TableCell>
                <TableCell className="text-right tabular">{gbp(o.won_mrr)}</TableCell>
                <TableCell className="text-right tabular">{gbp(o.lost_mrr)}</TableCell>
              </TableRow>
            ))}
            {!d.by_owner.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-cass-slate">No decided proposals in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reference</TableHead><TableHead>Client</TableHead><TableHead>Owner</TableHead>
            <TableHead>Outcome</TableHead><TableHead>Date</TableHead>
            <TableHead className="text-right">MRR</TableHead><TableHead>Reason</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.reference + i} data-testid={`winloss-row-${i}`}>
                <TableCell className="font-medium text-cass-navy">{r.reference}</TableCell>
                <TableCell>{r.client_name}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner_name || "\u2014"}</TableCell>
                <TableCell><Badge variant={r.outcome === "Won" ? "default" : "destructive"} className="text-[10px]">{r.outcome}</Badge></TableCell>
                <TableCell className="text-sm text-cass-slate">{fmtD(r.date)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.mrr)}</TableCell>
                <TableCell className="text-sm text-cass-slate max-w-[220px] truncate">{r.reason || "\u2014"}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-cass-slate">No decided proposals in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function OwnersReport({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-reps" value={d.reps} label="Team members" tone="navy" />
        <Kpi testId="kpi-tsent" value={d.total_sent} label="Proposals sent" tone="navy" />
        <Kpi testId="kpi-tacc" value={d.total_accepted} label="Accepted" tone="green" />
        <Kpi testId="kpi-tconv" value={`${d.avg_conversion}%`} label="Avg conversion" tone="green" />
        <Kpi testId="kpi-tmrr" value={gbp(d.total_mrr_won)} label="MRR won" tone="green" />
      </div>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Owner</TableHead><TableHead className="text-right">Sent</TableHead><TableHead className="text-right">Accepted</TableHead>
            <TableHead className="text-right">Conversion</TableHead><TableHead className="text-right">MRR won</TableHead>
            <TableHead className="text-right">One-off won</TableHead><TableHead className="text-right">Avg days</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r) => (
              <TableRow key={r.owner} data-testid={`owner-row-${r.owner}`}>
                <TableCell className="text-cass-navy font-medium">{r.owner}</TableCell>
                <TableCell className="text-right tabular">{r.sent}</TableCell>
                <TableCell className="text-right tabular text-emerald-600">{r.accepted}</TableCell>
                <TableCell className="text-right tabular">{r.conversion}%</TableCell>
                <TableCell className="text-right tabular">{gbp(r.mrr_won)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.oneoff_won)}</TableCell>
                <TableCell className="text-right tabular text-cass-slate">{r.avg_days}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={7} className="text-center py-10 text-cass-slate">No activity in this period.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function ClientBaseReport({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-clients" value={d.active_clients} label="Active clients" tone="navy" />
        <Kpi testId="kpi-cmrr" value={gbp(d.total_mrr)} label="Total live MRR" tone="green" />
        <Kpi testId="kpi-avgmrr" value={gbp(d.avg_mrr)} label="Avg MRR / client" tone="teal" />
        <Kpi testId="kpi-coneoff" value={gbp(d.oneoff_on_file)} label="One-off on file" tone="amber" />
        <Kpi testId="kpi-renset" value={d.renewals_set} label="Renewal dates set" tone="navy" />
      </div>
      <Card className="rounded-2xl border-border overflow-hidden" data-testid="clientbase-segments">
        <Table>
          <TableHeader><TableRow><TableHead>Segment</TableHead><TableHead className="text-right">Clients</TableHead><TableHead className="text-right">MRR</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.segments.map((sgm) => (
              <TableRow key={sgm.segment}><TableCell className="text-cass-navy">{sgm.segment}</TableCell><TableCell className="text-right tabular">{sgm.count}</TableCell><TableCell className="text-right tabular">{gbp(sgm.mrr)}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Type</TableHead><TableHead className="text-right">MRR</TableHead>
            <TableHead className="text-right">One-off</TableHead><TableHead className="text-right">Renewal</TableHead><TableHead>Proposal ref</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.client_name + i} data-testid={`clientbase-row-${i}`}>
                <TableCell className="text-cass-navy font-medium">{r.client_name}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{titleCase(r.type)}</Badge></TableCell>
                <TableCell className="text-right tabular">{gbp(r.mrr)}</TableCell>
                <TableCell className="text-right tabular">{gbp(r.oneoff)}</TableCell>
                <TableCell className="text-right tabular text-cass-slate">{fmtD(r.renewal_date)}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.reference}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-cass-slate">No active clients.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function LeadsReport({ d }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testId="kpi-openleads" value={d.open_count} label="Open leads" tone="navy" />
        <Kpi testId="kpi-expmrr" value={gbp(d.expected_mrr)} label="Expected MRR (open)" tone="teal" />
        <Kpi testId="kpi-wonleads" value={d.won_count} label="Won (period)" tone="green" />
        <Kpi testId="kpi-wonleadmrr" value={gbp(d.won_mrr)} label="Won MRR (period)" tone="green" />
        <Kpi testId="kpi-lostleads" value={d.lost_count} label="Lost (period)" tone="red" />
      </div>
      <Card className="rounded-2xl border-border overflow-hidden" data-testid="leads-bystage">
        <Table>
          <TableHeader><TableRow><TableHead>Stage</TableHead><TableHead className="text-right">Leads</TableHead><TableHead className="text-right">Expected MRR</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.by_stage.map((st) => (
              <TableRow key={st.stage}><TableCell className="text-cass-navy">{st.stage}</TableCell><TableCell className="text-right tabular">{st.count}</TableCell><TableCell className="text-right tabular">{gbp(st.expected)}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Card className="rounded-2xl border-border overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Owner</TableHead>
            <TableHead>Stage</TableHead><TableHead className="text-right">Expected fee</TableHead><TableHead>Close date</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.rows.map((r, i) => (
              <TableRow key={r.company + i} data-testid={`leads-row-${i}`}>
                <TableCell className="text-cass-navy font-medium">{r.company}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.contact_name || "—"}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.owner_name || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{r.stage}</Badge></TableCell>
                <TableCell className="text-right tabular">{gbp(r.expected_fee)}</TableCell>
                <TableCell className="text-sm text-cass-slate">{r.close_date || "—"}</TableCell>
              </TableRow>
            ))}
            {!d.rows.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-cass-slate">No open leads.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
